import { Epic } from 'redux-observable'
import { EMPTY, zip } from 'rxjs'
import { filter, map, mergeMap, pluck } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { EpicDependencies, RootState } from 'src/state/types'
import { partyApiEvents } from '../party'
import { PartyEvent } from '../party/state'
import { userApiSelectors } from '../user'
import { AgencyEvent, events } from './state'
import { ENDPOINTS } from './constants'
import { multiUserOperators } from '../../multiUser/state'
import { restfulErrorEvent } from 'utils/restful'
import {
  TAgencyEntity,
  TAgencyInvitation,
  TAgencyInvitationByToken,
  TAgencyListingItem,
  TAgencyMember,
  TAgencySegment,
  TAgencyTheme,
  TInvitableParty,
  TSignupInvite,
} from './types'

type AgencyEpic = Epic<AgencyEvent, AgencyEvent, RootState, EpicDependencies>

/**
 * @todo This could be a agency invite feature
 */
export const fetchPartiesWhenMembersFetched: Epic<
  AgencyEvent | PartyEvent,
  AgencyEvent | PartyEvent,
  RootState,
  EpicDependencies
> = action$ =>
  action$.pipe(
    filter(isOfType(events.users_success.type)),
    map(action => partyApiEvents.parties_request(action.payload.map((p: TAgencyMember) => p.userId))),
    tag('agency/epics/fetchPartiesWhenMembersFetched'),
  )

export const fetchAgency: AgencyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.agency_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.GET_AGENCY, state$, action.payload).pipe(
        pluck('response'),
        map((response: TAgencyEntity) => events.agency_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/apiFetchAgency'),
  )

export const fetchInvitations: AgencyEpic = (action$, state$, { get, catchRestError }) => {
  return action$.pipe(
    filter(isOfType(events.invitations_request.type)),
    multiUserOperators.filterOnboardedAndOwner(state$),
    mergeMap(action =>
      get(ENDPOINTS.GET_INVITATIONS, state$, {}).pipe(
        pluck('response'),
        map((response: TAgencyInvitation[]) => events.invitations_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/apiFetchInvitations'),
  )
}

export const getInvite: AgencyEpic = (action$, state$, { get, catchRestError }) => {
  return action$.pipe(
    filter(isOfType(events.invite_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.GET_INVITE, state$, action.payload).pipe(
        pluck('response'),
        map((response: TAgencyInvitationByToken) => events.invite_success(response, action.payload)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/getInvite'),
  )
}

export const getSignupInvite: AgencyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.signupInvite_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.GET_INVITE, state$, action.payload).pipe(
        pluck('response'),
        map((response: TSignupInvite) => events.signupInvite_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/getSignupInvite'),
  )

export const sendInvite: AgencyEpic = (action$, state$, { post, catchRestError }) => {
  return action$.pipe(
    filter(isOfType(events.sendInvite_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.SEND_INVITE, state$, action.payload, {}).pipe(
        pluck('response'),
        map((response: { id: string }) => events.sendInvite_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/sendInvite'),
  )
}

export const resendInvite: AgencyEpic = (action$, state$, { post, catchRestError }) => {
  return action$.pipe(
    filter(isOfType(events.resendInvite_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.RESEND_INVITE, state$, null, action.payload).pipe(
        pluck('response'),
        map((response: TAgencyInvitation) => events.resendInvite_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/resendInvite'),
  )
}

export const sendPartyInvite: AgencyEpic = (action$, state$, { post, catchRestError }) => {
  return action$.pipe(
    filter(isOfType(events.sendPartyInvite_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.SEND_PARTY_INVITE, state$, action.payload.body, action.payload.params).pipe(
        pluck('response'),
        map((response: { id: string }) => events.sendPartyInvite_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/sendPartyInvite'),
  )
}

export const revokeInvite: AgencyEpic = (action$, state$, { remove, catchRestError }) => {
  return action$.pipe(
    filter(isOfType(events.revokeInvite_request.type)),
    mergeMap(action =>
      remove(ENDPOINTS.REVOKE_INVITE, state$, action.payload).pipe(
        pluck('response'),
        map((response: TAgencyInvitation) => events.revokeInvite_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/revokeInvite'),
  )
}

export const getUsers: AgencyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.users_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.GET_USERS, state$, {}).pipe(
        pluck('response'),
        map((response: TAgencyMember[]) => events.users_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/getUsers'),
  )

export const getThemes: AgencyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.themes_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.GET_THEMES, state$, {}).pipe(
        pluck('response'),
        map((response: TAgencyTheme[]) => events.themes_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/getThemes'),
  )

/**
 * @todo This could be a agency settings
 */
export const updateAgencyTheme: AgencyEpic = (action$, state$, { put, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.updateBranding_request.type)),
    mergeMap(action => {
      const { logo, theme } = action.payload
      // The branding form's schema requires a theme, so a real submit always
      // carries one. Guarding rather than destructuring blind keeps a malformed
      // dispatch from throwing inside the epic and tearing down the stream.
      if (!theme) return EMPTY
      const { name, color } = theme
      const endpoint = name.includes('Custom') ? ENDPOINTS.SET_CUSTOM_THEME : ENDPOINTS.SET_THEME
      const payload = name.includes('Custom')
        ? {
            name,
            color: color.replace('#', ''),
            primary: 100,
            secondary: 40,
            accent: 24,
            tint: 8,
            button: 80,
            fontColor: 'FFFFFF',
          }
        : { name }
      return zip(
        put(endpoint, state$, payload, {}).pipe(
          pluck('response'),
          map((response: TAgencyTheme) => events.themeSet(response)),
          catchRestError(action),
        ),
        put(ENDPOINTS.UPDATE_LOGO, state$, { logoUrl: logo }, {}).pipe(
          pluck('response'),
          map((response: { logoUrl: string }) => events.logoUpdated(response)),
          catchRestError(action),
        ),
      ).pipe(
        mergeMap((zipped: AgencyEvent[]) => {
          const hasErrors = zipped.filter(e => e.type === restfulErrorEvent.toString()).length
          const _events: AgencyEvent[] = [...zipped]

          if (!hasErrors) {
            _events.push(events.updateBranding_success(null))
            _events.push(events.agency_request({ id: userApiSelectors.getCurrentAgencyId(state$.value) as string }))
          }
          return _events
        }),
      )
    }),
    tag('agency/epics/updateAgencyTheme'),
  )

/**
 * @todo This could be a agency settings
 */
export const updateAgency: AgencyEpic = (action$, state$, { put, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.updateAgency_request.type)),
    mergeMap(action =>
      put(ENDPOINTS.UPDATE_AGENCY, state$, action.payload.values, { id: action.payload.id }).pipe(
        pluck('response'),
        map((response: TAgencyEntity) => events.updateAgency_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/updateAgency'),
  )

export const fetchAgencyListing: AgencyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.agencyListing_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.AGENCY_LISTING, state$, {}).pipe(
        pluck('response'),
        map((response: TAgencyListingItem[]) => events.agencyListing_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/fetchAgencyListing'),
  )

export const fetchInvitableParties: AgencyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.invitableParties_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.INVITABLE_PARTIES, state$, {}).pipe(
        pluck('response'),
        map((response: TInvitableParty[]) => events.invitableParties_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/fetchInvitableParties'),
  )

export const getSegments: AgencyEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.getSegments_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.SEGMENT_BASE, state$, {}).pipe(
        pluck('response'),
        map((response: TAgencySegment[]) => events.getSegments_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/getSegments'),
  )

export const createSegment: AgencyEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.createSegment_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.SEGMENT_BASE, state$, action.payload, {}).pipe(
        pluck('response'),
        map((response: TAgencySegment[]) => events.createSegment_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/createSegment'),
  )

export const deleteSegment: AgencyEpic = (action$, state$, { remove, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.deleteSegment_request.type)),
    mergeMap(action =>
      remove(ENDPOINTS.SEGMENT_ENTITY, state$, action.payload).pipe(
        pluck('response'),
        map((response: TAgencySegment[]) => events.deleteSegment_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/deleteSegment'),
  )

export const deactivateAgency: AgencyEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.deactivateAgency_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.DEACTIVATE, state$, {}, action.payload).pipe(
        pluck('response'),
        map(() => events.deactivateAgency_success(action.payload)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/deactivateAgency'),
  )

export const activateAgency: AgencyEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.activateAgency_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.ACTIVATE, state$, {}, action.payload).pipe(
        pluck('response'),
        map(() => events.activateAgency_success(action.payload)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/activateAgency'),
  )

export const addToGroup: AgencyEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.addToGroup_request.type)),
    mergeMap(action => {
      const { params, body } = action.payload
      return post(ENDPOINTS.AGENCY_TO_AGENCY_GROUP, state$, body, params).pipe(
        pluck('response'),
        map((response: unknown) => events.addToGroup_success(response)),
        catchRestError(action),
      )
    }),
    tag('agency/epics/addToGroup'),
  )

export const removeFromGroup: AgencyEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.removeFromGroup_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.REMOVE_AGENCY_FROM_AGENCY_GROUP, state$, {}, action.payload.params).pipe(
        pluck('response'),
        map((response: unknown) => events.removeFromGroup_success(response)),
        catchRestError(action),
      ),
    ),
    tag('agency/epics/removeFromGroup'),
  )
